import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import { ImageNotSupported } from '@mui/icons-material';
import LivePreview from './LivePreview.jsx';
import { PALETTE } from '../theme';

/**
 * SINGLE SOURCE OF TRUTH for the product-card image area.
 *
 * Both the QuickOrderStrip (inline 3-card view) and the ProductGallery
 * (expanded grid) used to roll their own version of this block, which made
 * it possible — and at one point did happen — for one path to pass `wrap`
 * to LivePreview while the other silently dropped it. That meant the cup's
 * cylindrical wrap only worked in one view. Centralising here ensures
 * every product card uses the same preview pipeline.
 *
 * Decides between three render modes:
 *   1. LivePreview canvas/img composite when `useLivePreview` is true.
 *      Forwards every preview-related prop from the product config
 *      (bounds, wrap, blend, fit, …) so flat overlays and cylindrical
 *      wraps both work.
 *   2. Static product image if there's no live preview but a `displayedImage`
 *      is available (e.g. the user hasn't shaped a photo yet).
 *   3. A "drop the file in src/images/products/" placeholder if no image
 *      file exists. Helps when adding a new product.
 */
const ProductPreviewArea = ({
    product,
    designSrc,
    livePreviewCaption,
    liveBounds,
    displayedImage,
}) => {
    // Render the empty base image even BEFORE the user has shaped a photo so
    // the cup itself never visually changes — the design+caption fade in on
    // top of an already-present empty cup. An earlier version cross-faded
    // the populated Zazzle stock photo into the empty-base composite, but
    // that meant two different cup photos blended through each other, which
    // read as a flicker rather than a "design settling on the product".
    const useLivePreview = !!(product.livePreview && product.livePreview.baseImage);

    if (useLivePreview) {
        return (
            <LivePreview
                baseSrc={product.livePreview.baseImage}
                designSrc={designSrc}
                bounds={liveBounds}
                designBlendMode={product.livePreview.designBlendMode}
                designOpacity={product.livePreview.designOpacity}
                captionText={livePreviewCaption}
                caption={product.livePreview.caption}
                designFit={product.livePreview.designFit}
                designPosition={product.livePreview.designPosition}
                wrap={product.livePreview.wrap}
                alt={product.name}
            />
        );
    }

    if (displayedImage) {
        return (
            <img
                src={displayedImage}
                alt={product.name}
                style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                }}
            />
        );
    }

    return (
        <Box sx={{ textAlign: 'center', px: 2 }}>
            <ImageNotSupported sx={{ fontSize: 40, opacity: 0.5 }} />
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: PALETTE.textFaint }}>
                Drop {product.imageFile} into
                <br />src/images/products/
            </Typography>
        </Box>
    );
};

ProductPreviewArea.propTypes = {
    // eslint-disable-next-line react/forbid-prop-types
    product: PropTypes.object.isRequired,
    designSrc: PropTypes.string,
    livePreviewCaption: PropTypes.string,
    // eslint-disable-next-line react/forbid-prop-types
    liveBounds: PropTypes.object,
    displayedImage: PropTypes.string,
};

/**
 * Compute the bounds + caption text a product card should pass into the
 * preview area. Centralised so the rules — "use designCaptioned when a
 * caption is present, otherwise design"; "skip caption when noLabel is
 * checked" — live in one place.
 */
export function resolvePreviewProps(product, { designSrc, caption, noLabel }) {
    // `useLivePreview` (= "render the LivePreview component") is now gated on
    // baseImage alone — we show the empty cup before the user has shaped
    // anything. The caption + bounds still depend on designSrc so that an
    // unshaped product doesn't render a floating caption on a blank cup.
    const useLivePreview = !!(product.livePreview && product.livePreview.baseImage);
    const livePreviewCaption =
        useLivePreview && designSrc && product.supportsLabel && !noLabel ? caption : '';
    const liveBounds = useLivePreview
        ? (livePreviewCaption && product.livePreview.designCaptioned)
            ? product.livePreview.designCaptioned
            : product.livePreview.design
        : null;
    return { useLivePreview, livePreviewCaption, liveBounds };
}

export default ProductPreviewArea;
